import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/EditOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiClient } from '../../api/client';
import { useAuth } from '../../context/authContext';
import { TableToolbar } from '../../components/table/TableToolbar';
import { PageHeader } from '../../components/PageHeader';
import { CourseDocumentRequirementsDialog } from './CourseDocumentRequirementsDialog';

type EntityKind = 'university' | 'faculty' | 'course';
const RESOURCE_PATH: Record<EntityKind, string> = {
  university: 'universities',
  faculty: 'faculties',
  course: 'courses',
};

interface CountryOption {
  id: number;
  name: string;
}
interface UniversityRow {
  id: number;
  name: string;
  city: string | null;
  website: string | null;
  description: string | null;
  countryId: number;
  isActive: boolean;
  country?: { id: number; name: string } | null;
}
interface FacultyRow {
  id: number;
  name: string;
  universityId: number;
  isActive: boolean;
}
interface CourseRow {
  id: number;
  name: string;
  durationMonths: number | null;
  tuitionFee: string | null;
  isFoundationExam: boolean;
  description: string | null;
  universityId: number;
  university: { id: number; name: string };
  facultyId: number | null;
  faculty: { id: number; name: string } | null;
  academicLevelId?: number;
  academicLevel: { id: number; name: string };
  currency: { id: number; code: string } | null;
  isActive: boolean;
}
type EntityRow = UniversityRow | FacultyRow | CourseRow;
interface AcademicLevelOption {
  id: number;
  name: string;
}
interface CurrencyOption {
  id: number;
  code: string;
}
interface UsageCounts {
  applications: number;
  students: number;
  leads?: number;
  courses?: number;
}

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};

export const AcademicCatalogPage = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('master_data', 'update');
  const canCreate = hasPermission('master_data', 'create');

  // 'ALL' lists (and searches) universities of every country at once.
  const [countryId, setCountryId] = useState<number | '' | 'ALL'>('');
  const [universityId, setUniversityId] = useState<number | ''>('');
  const [uniSearch, setUniSearch] = useState('');
  const [facultySearch, setFacultySearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [showInactiveUnis, setShowInactiveUnis] = useState(false);
  const [showInactiveFaculties, setShowInactiveFaculties] = useState(false);
  const [showInactiveCourses, setShowInactiveCourses] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add University dialog
  const [addUniOpen, setAddUniOpen] = useState(false);
  const [uniName, setUniName] = useState('');
  const [uniCity, setUniCity] = useState('');
  const [uniWebsite, setUniWebsite] = useState('');
  const [uniDescription, setUniDescription] = useState('');

  // Add Faculty dialog
  const [addFacultyOpen, setAddFacultyOpen] = useState(false);
  const [facultyName, setFacultyName] = useState('');

  // Add Program (Course) dialog
  const [addCourseOpen, setAddCourseOpen] = useState(false);
  const [courseName, setCourseName] = useState('');
  const [courseLevelId, setCourseLevelId] = useState<number | ''>('');
  const [courseDuration, setCourseDuration] = useState('');
  const [courseFoundationExam, setCourseFoundationExam] = useState(false);
  const [courseFee, setCourseFee] = useState('');
  const [courseCurrencyId, setCourseCurrencyId] = useState<number | ''>('');
  const [courseDescription, setCourseDescription] = useState('');

  // Row-level ⋮ menu state (shared shape for all three grids, distinguished by `kind`)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTarget, setMenuTarget] = useState<{ kind: EntityKind; row: EntityRow } | null>(null);

  // Edit dialog (shared for all three kinds)
  const [editTarget, setEditTarget] = useState<{ kind: EntityKind; row: EntityRow } | null>(null);

  // View-details dialog (shared for all three kinds)
  const [detailsTarget, setDetailsTarget] = useState<{ kind: EntityKind; row: EntityRow } | null>(null);

  // Deactivate-confirmation dialog (only for the destructive direction — reactivating never needs one)
  const [deactivateTarget, setDeactivateTarget] = useState<{ kind: EntityKind; row: EntityRow } | null>(null);

  // Document Requirements dialog (Phase 60) — Course-only, since
  // CourseDocumentRequirement is scoped to a Course, never a University.
  const [docReqTarget, setDocReqTarget] = useState<CourseRow | null>(null);

  const countriesQuery = useQuery({
    queryKey: ['lookup', '/master-data/countries'],
    queryFn: async () => (await apiClient.get('/master-data/countries')).data.data as CountryOption[],
  });
  const levelsQuery = useQuery({
    queryKey: ['lookup', '/master-data/academic-levels'],
    queryFn: async () =>
      (await apiClient.get('/master-data/academic-levels')).data.data as AcademicLevelOption[],
  });
  const currenciesQuery = useQuery({
    queryKey: ['lookup', '/master-data/currencies'],
    queryFn: async () => (await apiClient.get('/master-data/currencies')).data.data as CurrencyOption[],
  });

  const universitiesQuery = useQuery({
    queryKey: ['universities', countryId, uniSearch, showInactiveUnis],
    queryFn: async () =>
      (
        await apiClient.get('/master-data/universities', {
          params: {
            countryId: countryId === 'ALL' ? undefined : countryId,
            search: uniSearch || undefined,
            activeOnly: showInactiveUnis ? undefined : true,
          },
        })
      ).data.data as UniversityRow[],
    enabled: countryId !== '',
  });

  const facultiesQuery = useQuery({
    queryKey: ['faculties', universityId, facultySearch, showInactiveFaculties],
    queryFn: async () =>
      (
        await apiClient.get('/master-data/faculties', {
          params: { universityId, activeOnly: showInactiveFaculties ? undefined : true },
        })
      ).data.data as FacultyRow[],
    enabled: universityId !== '',
  });

  // Student Application correction: Programs are scoped directly to the
  // selected University (Course.universityId, always required) — no
  // longer routed through an intermediate Faculty selection. Faculty
  // management itself (the middle column) is untouched and still
  // available for whoever wants to organize by it.
  const coursesQuery = useQuery({
    queryKey: ['courses', universityId, courseSearch, showInactiveCourses],
    queryFn: async () =>
      (
        await apiClient.get('/master-data/courses', {
          params: {
            universityId,
            search: courseSearch || undefined,
            activeOnly: showInactiveCourses ? undefined : true,
          },
        })
      ).data.data as CourseRow[],
    enabled: universityId !== '',
  });

  const usageQuery = useQuery({
    queryKey: ['master-data-usage', deactivateTarget?.kind, deactivateTarget?.row.id],
    queryFn: async () =>
      (
        await apiClient.get(`/master-data/${RESOURCE_PATH[deactivateTarget!.kind]}/${deactivateTarget!.row.id}/usage`)
      ).data.data as UsageCounts,
    enabled: !!deactivateTarget,
  });

  const invalidateUniversities = () => queryClient.invalidateQueries({ queryKey: ['universities'] });
  const invalidateFaculties = () => queryClient.invalidateQueries({ queryKey: ['faculties'] });
  const invalidateCourses = () => queryClient.invalidateQueries({ queryKey: ['courses'] });

  const createUniversityMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/master-data/universities', {
        name: uniName,
        countryId,
        city: uniCity || undefined,
        website: uniWebsite || undefined,
        description: uniDescription || undefined,
      }),
    onSuccess: () => {
      invalidateUniversities();
      setAddUniOpen(false);
      setUniName('');
      setUniCity('');
      setUniWebsite('');
      setUniDescription('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const createFacultyMutation = useMutation({
    mutationFn: () => apiClient.post('/master-data/faculties', { name: facultyName, universityId }),
    onSuccess: () => {
      invalidateFaculties();
      setAddFacultyOpen(false);
      setFacultyName('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const createCourseMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/master-data/courses', {
        name: courseName,
        universityId,
        academicLevelId: courseLevelId,
        durationMonths: courseDuration || undefined,
        tuitionFee: courseFee || undefined,
        currencyId: courseCurrencyId || undefined,
        description: courseDescription || undefined,
        isFoundationExam: courseFoundationExam,
      }),
    onSuccess: () => {
      invalidateCourses();
      setAddCourseOpen(false);
      setCourseName('');
      setCourseFoundationExam(false);
      setCourseLevelId('');
      setCourseDuration('');
      setCourseFee('');
      setCourseCurrencyId('');
      setCourseDescription('');
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const updateUniversityMutation = useMutation({
    mutationFn: (payload: { id: number; values: Record<string, unknown> }) =>
      apiClient.patch(`/master-data/universities/${payload.id}`, payload.values),
    onSuccess: () => {
      invalidateUniversities();
      setEditTarget(null);
      setDeactivateTarget(null);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const updateFacultyMutation = useMutation({
    mutationFn: (payload: { id: number; values: Record<string, unknown> }) =>
      apiClient.patch(`/master-data/faculties/${payload.id}`, payload.values),
    onSuccess: () => {
      invalidateFaculties();
      setEditTarget(null);
      setDeactivateTarget(null);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const updateCourseMutation = useMutation({
    mutationFn: (payload: { id: number; values: Record<string, unknown> }) =>
      apiClient.patch(`/master-data/courses/${payload.id}`, payload.values),
    onSuccess: () => {
      invalidateCourses();
      setEditTarget(null);
      setDeactivateTarget(null);
      setError(null);
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const mutationForKind = (kind: EntityKind) =>
    kind === 'university' ? updateUniversityMutation : kind === 'faculty' ? updateFacultyMutation : updateCourseMutation;

  const openMenu = (kind: EntityKind, row: EntityRow, target: HTMLElement) => {
    setMenuAnchor(target);
    setMenuTarget({ kind, row });
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTarget(null);
  };

  const handleReactivate = (kind: EntityKind, row: EntityRow) => {
    closeMenu();
    setError(null);
    mutationForKind(kind).mutate({ id: row.id, values: { isActive: true } });
  };

  const statusColumn = <T extends EntityRow>(): GridColDef<T> => ({
    field: 'isActive',
    headerName: 'Status',
    width: 110,
    renderCell: (params) => (
      <Chip size="small" label={params.value ? 'Active' : 'Inactive'} color={params.value ? 'success' : 'default'} />
    ),
  });
  const actionsColumn = <T extends EntityRow>(kind: EntityKind): GridColDef<T> => ({
    field: 'actions',
    headerName: 'Actions',
    width: 80,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <IconButton
        size="small"
        aria-label={`Actions for ${params.row.name}`}
        onClick={(e) => {
          e.stopPropagation();
          openMenu(kind, params.row, e.currentTarget);
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    ),
  });

  const universityColumns: GridColDef<UniversityRow>[] = [
    { field: 'name', headerName: 'University', flex: 1 },
    ...(countryId === 'ALL'
      ? [
          {
            field: 'country',
            headerName: 'Country',
            width: 120,
            valueGetter: (_v: unknown, row: UniversityRow) => row.country?.name ?? '',
          } as GridColDef<UniversityRow>,
        ]
      : []),
    { field: 'city', headerName: 'City', width: 120 },
    statusColumn<UniversityRow>(),
    actionsColumn<UniversityRow>('university'),
  ];
  const facultyColumns: GridColDef<FacultyRow>[] = [
    { field: 'name', headerName: 'Faculty', flex: 1 },
    statusColumn<FacultyRow>(),
    actionsColumn<FacultyRow>('faculty'),
  ];
  const courseColumns: GridColDef<CourseRow>[] = [
    { field: 'name', headerName: 'Program', flex: 1 },
    {
      field: 'academicLevel',
      headerName: 'Level',
      width: 130,
      valueGetter: (_v, row) => row.academicLevel.name,
    },
    { field: 'durationMonths', headerName: 'Duration (mo)', width: 110 },
    { field: 'isFoundationExam', headerName: 'Foundation Exam', width: 130, type: 'boolean' },
    {
      field: 'tuitionFee',
      headerName: 'Tuition',
      width: 130,
      valueGetter: (_v, row) => (row.tuitionFee ? `${row.currency?.code ?? ''} ${row.tuitionFee}` : '—'),
    },
    statusColumn<CourseRow>(),
    actionsColumn<CourseRow>('course'),
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={SchoolOutlinedIcon}
        color="#12B76A"
        title="Universities & Courses"
        subtitle="Manage universities, faculties and their program catalog."
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                select
                label="Country"
                size="small"
                sx={{ width: 160 }}
                value={countryId}
                onChange={(e) => {
                  setCountryId(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value));
                  setUniversityId('');
                }}
              >
                <MenuItem value="ALL">
                  <em>All countries</em>
                </MenuItem>
                {countriesQuery.data?.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Search name/city"
                value={uniSearch}
                onChange={(e) => setUniSearch(e.target.value)}
                sx={{ width: 150 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={showInactiveUnis}
                    onChange={(e) => setShowInactiveUnis(e.target.checked)}
                  />
                }
                label="Show inactive"
              />
            </Stack>
            {canCreate && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                // A new university belongs to one country — pick it first.
                disabled={countryId === '' || countryId === 'ALL'}
                onClick={() => {
                  setError(null);
                  setAddUniOpen(true);
                }}
              >
                Add university
              </Button>
            )}
          </Stack>
          <Box sx={{ height: 420, bgcolor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={universitiesQuery.data ?? []}
              columns={universityColumns}
              loading={universitiesQuery.isFetching}
              slots={{ toolbar: TableToolbar }}
              onRowClick={(params) => setUniversityId(params.row.id)}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1">
                Faculties {universityId ? '' : '(select a university)'}
              </Typography>
              {universityId !== '' && (
                <>
                  <TextField
                    size="small"
                    label="Search name"
                    value={facultySearch}
                    onChange={(e) => setFacultySearch(e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showInactiveFaculties}
                        onChange={(e) => setShowInactiveFaculties(e.target.checked)}
                      />
                    }
                    label="Show inactive"
                  />
                </>
              )}
            </Stack>
            {canCreate && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={universityId === ''}
                onClick={() => {
                  setError(null);
                  setAddFacultyOpen(true);
                }}
              >
                Add Faculty
              </Button>
            )}
          </Stack>
          <Box sx={{ height: 420, bgcolor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={
                facultySearch
                  ? (facultiesQuery.data ?? []).filter((f) => f.name.toLowerCase().includes(facultySearch.toLowerCase()))
                  : facultiesQuery.data ?? []
              }
              columns={facultyColumns}
              loading={facultiesQuery.isFetching}
              slots={{ toolbar: TableToolbar }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" gap={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle1">
                Programs {universityId ? '' : '(select a university)'}
              </Typography>
              {universityId !== '' && (
                <>
                  <TextField
                    size="small"
                    label="Search name/level"
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={showInactiveCourses}
                        onChange={(e) => setShowInactiveCourses(e.target.checked)}
                      />
                    }
                    label="Show inactive"
                  />
                </>
              )}
            </Stack>
            {canCreate && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                disabled={universityId === ''}
                onClick={() => {
                  setError(null);
                  setAddCourseOpen(true);
                }}
              >
                Add Program
              </Button>
            )}
          </Stack>
          <Box sx={{ height: 420, bgcolor: 'background.paper', borderRadius: 1 }}>
            <DataGrid
              rows={
                courseSearch
                  ? (coursesQuery.data ?? []).filter(
                      (c) =>
                        c.name.toLowerCase().includes(courseSearch.toLowerCase()) ||
                        c.academicLevel.name.toLowerCase().includes(courseSearch.toLowerCase()),
                    )
                  : coursesQuery.data ?? []
              }
              columns={courseColumns}
              loading={coursesQuery.isFetching}
              slots={{ toolbar: TableToolbar }}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Row actions menu — shared by all three grids */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {menuTarget && [
          canManage && (
            <MenuItem
              key="edit"
              onClick={() => {
                setEditTarget(menuTarget);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          ),
          <MenuItem
            key="details"
            onClick={() => {
              setDetailsTarget(menuTarget);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View details</ListItemText>
          </MenuItem>,
          menuTarget.kind === 'course' && (
            <MenuItem
              key="document-requirements"
              onClick={() => {
                setDocReqTarget(menuTarget.row as CourseRow);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <DescriptionOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Document Requirements</ListItemText>
            </MenuItem>
          ),
          canManage &&
            (menuTarget.row.isActive ? (
              <MenuItem
                key="deactivate"
                onClick={() => {
                  setDeactivateTarget(menuTarget);
                  closeMenu();
                }}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Deactivate</ListItemText>
              </MenuItem>
            ) : (
              <MenuItem key="reactivate" onClick={() => handleReactivate(menuTarget.kind, menuTarget.row)}>
                <ListItemIcon>
                  <CheckCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Reactivate</ListItemText>
              </MenuItem>
            )),
        ]}
      </Menu>

      {/* Add University */}
      <Dialog open={addUniOpen} onClose={() => setAddUniOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New university</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" autoFocus fullWidth value={uniName} onChange={(e) => setUniName(e.target.value)} />
            <TextField label="City" fullWidth value={uniCity} onChange={(e) => setUniCity(e.target.value)} />
            <TextField label="Website" fullWidth value={uniWebsite} onChange={(e) => setUniWebsite(e.target.value)} />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={uniDescription}
              onChange={(e) => setUniDescription(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUniOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!uniName.trim() || createUniversityMutation.isPending}
            onClick={() => createUniversityMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Faculty — University is fixed to the currently-selected one */}
      <Dialog open={addFacultyOpen} onClose={() => setAddFacultyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New faculty</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="University"
              fullWidth
              disabled
              value={universitiesQuery.data?.find((u) => u.id === universityId)?.name ?? ''}
            />
            <TextField
              label="Faculty name"
              autoFocus
              fullWidth
              value={facultyName}
              onChange={(e) => setFacultyName(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFacultyOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!facultyName.trim() || createFacultyMutation.isPending}
            onClick={() => createFacultyMutation.mutate()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Program — University is fixed to the currently-selected one */}
      <Dialog open={addCourseOpen} onClose={() => setAddCourseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New program</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="University"
              fullWidth
              disabled
              value={universitiesQuery.data?.find((u) => u.id === universityId)?.name ?? ''}
            />
            <TextField label="Program name" autoFocus fullWidth value={courseName} onChange={(e) => setCourseName(e.target.value)} />
            <TextField
              select
              label="Academic level"
              fullWidth
              value={courseLevelId}
              onChange={(e) => setCourseLevelId(Number(e.target.value))}
            >
              {levelsQuery.data?.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Duration (months)"
              type="number"
              fullWidth
              value={courseDuration}
              onChange={(e) => setCourseDuration(e.target.value)}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Tuition fee"
                type="number"
                fullWidth
                value={courseFee}
                onChange={(e) => setCourseFee(e.target.value)}
              />
              <TextField
                select
                label="Currency"
                fullWidth
                value={courseCurrencyId}
                onChange={(e) => setCourseCurrencyId(Number(e.target.value))}
              >
                {currenciesQuery.data?.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.code}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={courseDescription}
              onChange={(e) => setCourseDescription(e.target.value)}
            />
            <FoundationExamCheckbox checked={courseFoundationExam} onChange={setCourseFoundationExam} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCourseOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!courseName.trim() || !courseLevelId || createCourseMutation.isPending}
            onClick={() => createCourseMutation.mutate()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit University / Faculty / Program */}
      {editTarget && editTarget.kind === 'university' && (
        <EditUniversityDialog
          row={editTarget.row as UniversityRow}
          countries={countriesQuery.data ?? []}
          error={error}
          submitting={updateUniversityMutation.isPending}
          onClose={() => {
            setEditTarget(null);
            setError(null);
          }}
          onSave={(values) => {
            setError(null);
            updateUniversityMutation.mutate({ id: editTarget.row.id, values });
          }}
        />
      )}
      {editTarget && editTarget.kind === 'faculty' && (
        <EditFacultyDialog
          row={editTarget.row as FacultyRow}
          error={error}
          submitting={updateFacultyMutation.isPending}
          onClose={() => {
            setEditTarget(null);
            setError(null);
          }}
          onSave={(values) => {
            setError(null);
            updateFacultyMutation.mutate({ id: editTarget.row.id, values });
          }}
        />
      )}
      {editTarget && editTarget.kind === 'course' && (
        <EditCourseDialog
          row={editTarget.row as CourseRow}
          levels={levelsQuery.data ?? []}
          currencies={currenciesQuery.data ?? []}
          universities={universitiesQuery.data ?? []}
          error={error}
          submitting={updateCourseMutation.isPending}
          onClose={() => {
            setEditTarget(null);
            setError(null);
          }}
          onSave={(values) => {
            setError(null);
            updateCourseMutation.mutate({ id: editTarget.row.id, values });
          }}
        />
      )}

      {/* View details */}
      <Dialog open={!!detailsTarget} onClose={() => setDetailsTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {detailsTarget?.kind === 'university'
            ? 'University details'
            : detailsTarget?.kind === 'faculty'
              ? 'Faculty details'
              : 'Program details'}
        </DialogTitle>
        <DialogContent>
          {detailsTarget?.kind === 'university' && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Name:</strong> {(detailsTarget.row as UniversityRow).name}</Typography>
              <Typography variant="body2"><strong>City:</strong> {(detailsTarget.row as UniversityRow).city || '—'}</Typography>
              <Typography variant="body2"><strong>Website:</strong> {(detailsTarget.row as UniversityRow).website || '—'}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {detailsTarget.row.isActive ? 'Active' : 'Inactive'}</Typography>
              <Typography variant="body2"><strong>Description:</strong> {(detailsTarget.row as UniversityRow).description || '—'}</Typography>
            </Stack>
          )}
          {detailsTarget?.kind === 'faculty' && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Name:</strong> {(detailsTarget.row as FacultyRow).name}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {detailsTarget.row.isActive ? 'Active' : 'Inactive'}</Typography>
            </Stack>
          )}
          {detailsTarget?.kind === 'course' && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>Name:</strong> {(detailsTarget.row as CourseRow).name}</Typography>
              <Typography variant="body2"><strong>University:</strong> {(detailsTarget.row as CourseRow).university.name}</Typography>
              <Typography variant="body2"><strong>Faculty:</strong> {(detailsTarget.row as CourseRow).faculty?.name || '—'}</Typography>
              <Typography variant="body2"><strong>Level:</strong> {(detailsTarget.row as CourseRow).academicLevel.name}</Typography>
              <Typography variant="body2"><strong>Duration:</strong> {(detailsTarget.row as CourseRow).durationMonths ?? '—'} months</Typography>
              <Typography variant="body2">
                <strong>Tuition:</strong>{' '}
                {(detailsTarget.row as CourseRow).tuitionFee
                  ? `${(detailsTarget.row as CourseRow).currency?.code ?? ''} ${(detailsTarget.row as CourseRow).tuitionFee}`
                  : '—'}
              </Typography>
              <Typography variant="body2"><strong>Status:</strong> {detailsTarget.row.isActive ? 'Active' : 'Inactive'}</Typography>
              <Typography variant="body2"><strong>Description:</strong> {(detailsTarget.row as CourseRow).description || '—'}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation, with dependent-record counts */}
      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Deactivate{' '}
          {deactivateTarget?.kind === 'university' ? 'University' : deactivateTarget?.kind === 'faculty' ? 'Faculty' : 'Program'}?
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <DialogContentText>
            <strong>{deactivateTarget?.row.name}</strong> is currently used by{' '}
            {usageQuery.isLoading
              ? '…'
              : `${usageQuery.data?.applications ?? 0} application(s) and ${usageQuery.data?.students ?? 0} student(s)`}
            {deactivateTarget?.kind !== 'faculty' && usageQuery.data?.leads !== undefined
              ? ` and ${usageQuery.data.leads} lead(s)`
              : ''}
            {deactivateTarget?.kind === 'university' && usageQuery.data
              ? `, and has ${usageQuery.data.courses ?? 0} course(s) under it`
              : ''}
            {deactivateTarget?.kind === 'faculty' && usageQuery.data
              ? `, and has ${usageQuery.data.courses ?? 0} program(s) under it`
              : ''}
            .
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            Deactivating it will prevent it from being selected for new applications, but existing records will
            remain unchanged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={mutationForKind(deactivateTarget?.kind ?? 'university').isPending}
            onClick={() => {
              if (!deactivateTarget) return;
              setError(null);
              mutationForKind(deactivateTarget.kind).mutate({ id: deactivateTarget.row.id, values: { isActive: false } });
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {docReqTarget && (
        <CourseDocumentRequirementsDialog
          courseId={docReqTarget.id}
          courseName={docReqTarget.name}
          canEdit={canCreate}
          onClose={() => setDocReqTarget(null)}
        />
      )}
    </Box>
  );
};

interface EditUniversityDialogProps {
  row: UniversityRow;
  countries: CountryOption[];
  error: string | null;
  submitting: boolean;
  onClose: () => void;
  onSave: (values: Record<string, unknown>) => void;
}

const EditUniversityDialog = ({ row, countries, error, submitting, onClose, onSave }: EditUniversityDialogProps) => {
  const [name, setName] = useState(row.name);
  const [city, setCity] = useState(row.city ?? '');
  const [website, setWebsite] = useState(row.website ?? '');
  const [description, setDescription] = useState(row.description ?? '');
  const [countryId, setCountryId] = useState<number>(row.countryId);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit university</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Country" fullWidth value={countryId} onChange={(e) => setCountryId(Number(e.target.value))}>
            {countries.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="University name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="City" fullWidth value={city} onChange={(e) => setCity(e.target.value)} />
          <TextField label="Website" fullWidth value={website} onChange={(e) => setWebsite(e.target.value)} />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || submitting}
          onClick={() =>
            onSave({
              name,
              countryId,
              city: city || undefined,
              website: website || '',
              description: description || '',
            })
          }
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface EditFacultyDialogProps {
  row: FacultyRow;
  error: string | null;
  submitting: boolean;
  onClose: () => void;
  onSave: (values: Record<string, unknown>) => void;
}

/** University is deliberately not editable here — moving a Faculty between
 * Universities is a controlled operation, not a casual edit (spec §3);
 * the backend also rejects it once the Faculty has real references. */
const EditFacultyDialog = ({ row, error, submitting, onClose, onSave }: EditFacultyDialogProps) => {
  const [name, setName] = useState(row.name);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit faculty</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Faculty name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || submitting} onClick={() => onSave({ name })}>
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** FIS Exam (Phase 93): students on a programme with this ticked are listed
 * under CRM → FIS Exam and are the only ones shown/charged the FIS Exam Fee. */
const FoundationExamCheckbox = ({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />}
    label="Foundation Exam programme (FIS Exam Fee applies)"
  />
);

interface EditCourseDialogProps {
  row: CourseRow;
  levels: AcademicLevelOption[];
  currencies: CurrencyOption[];
  universities: UniversityRow[];
  error: string | null;
  submitting: boolean;
  onClose: () => void;
  onSave: (values: Record<string, unknown>) => void;
}

/** Student Application correction (spec §12): University is now editable
 * here — changing it re-scopes which Student Applications display this
 * Program. The backend re-validates and, if this Program's Faculty no
 * longer belongs to the newly-chosen University, clears that now-stale
 * Faculty reference rather than blocking the change (see
 * courses.route.ts) — Faculty itself is otherwise not editable here,
 * matching the spec's own Edit Program field list. `universities` is
 * whatever list the page's own Country filter currently has loaded — the
 * same list "Add university" already offers. */
const EditCourseDialog = ({
  row,
  levels,
  currencies,
  universities,
  error,
  submitting,
  onClose,
  onSave,
}: EditCourseDialogProps) => {
  const [name, setName] = useState(row.name);
  const [universityId, setUniversityId] = useState<number>(row.universityId);
  const [academicLevelId, setAcademicLevelId] = useState<number>(row.academicLevel.id);
  const [durationMonths, setDurationMonths] = useState(row.durationMonths?.toString() ?? '');
  const [tuitionFee, setTuitionFee] = useState(row.tuitionFee ?? '');
  const [currencyId, setCurrencyId] = useState<number | ''>(row.currency?.id ?? '');
  const [description, setDescription] = useState(row.description ?? '');
  const [isFoundationExam, setIsFoundationExam] = useState(row.isFoundationExam);

  // The Program's own current University may not be in the caller's
  // currently-filtered list (a different Country filter, or simply not
  // yet loaded) — always include it as an option so the select never
  // shows a blank/invalid value for the row's own existing data.
  const universityOptions =
    universities.some((u) => u.id === row.universityId) || !row.university
      ? universities
      : [{ ...row.university, city: null, website: null, description: null, countryId: 0, isActive: true }, ...universities];

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit program</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="University"
            fullWidth
            value={universityId}
            onChange={(e) => setUniversityId(Number(e.target.value))}
          >
            {universityOptions.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
          {row.faculty && <TextField label="Faculty" fullWidth disabled value={row.faculty.name} />}
          <TextField label="Program name" fullWidth value={name} onChange={(e) => setName(e.target.value)} />
          <TextField
            select
            label="Academic level"
            fullWidth
            value={academicLevelId}
            onChange={(e) => setAcademicLevelId(Number(e.target.value))}
          >
            {levels.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Duration (months)"
            type="number"
            fullWidth
            value={durationMonths}
            onChange={(e) => setDurationMonths(e.target.value)}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Tuition fee"
              type="number"
              fullWidth
              value={tuitionFee}
              onChange={(e) => setTuitionFee(e.target.value)}
            />
            <TextField
              select
              label="Currency"
              fullWidth
              value={currencyId}
              onChange={(e) => setCurrencyId(Number(e.target.value))}
            >
              {currencies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.code}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FoundationExamCheckbox checked={isFoundationExam} onChange={setIsFoundationExam} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || !academicLevelId || submitting}
          onClick={() =>
            onSave({
              name,
              universityId,
              academicLevelId,
              durationMonths: durationMonths || undefined,
              tuitionFee: tuitionFee || undefined,
              currencyId: currencyId || undefined,
              description: description || '',
              isFoundationExam,
            })
          }
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
